/**
 * Contract party + scope derivation. Shared by ProjectWorkspace.computeContractSections and
 * buildContractPreviewFromProfile so a signed MSA never carries a value that did not come from
 * real data (Company attributes, the tenant's AdvisoryFirm record, project.standards, milestones).
 * Absent is acceptable; fabricated is not.
 */

/**
 * Values earlier builds wrote into saved scopingContractDraft rows as "fallbacks". They were never
 * real, so a draft loaded from the server has them blanked rather than shown to a client.
 */
export const LEGACY_FABRICATED_CONTRACT_VALUES = Object.freeze([
  "Nexus Corp Pvt. Ltd.",
  "AABCN1234P",
  "27AABCN1234P1Z5",
  "PolicyLense Advisory LLP",
  "AADPL5678Q",
  "27AADPL5678Q1ZR",
]);

const clean = (v) => (typeof v === "string" ? v.trim() : "");
const PARTY_KEYS = ["companyName", "companyPAN", "companyGST", "advisorName", "advisorPAN", "advisorGST", "advisorLogoUrl"];

/**
 * Blanks legacy fabricated party values in a saved draft. With dropBlankParties, a blank party
 * key is removed entirely so merging the draft never overwrites a real, derived value with "".
 */
export function stripLegacyFabricatedContractValues(draft, { dropBlankParties = false } = {}) {
  if (!draft || typeof draft !== "object") return draft;
  const out = { ...draft };
  delete out.advisorLogoUrl; // expiring SAS URL; always re-derived from the live firm record
  for (const k of PARTY_KEYS) {
    if (typeof out[k] === "string" && LEGACY_FABRICATED_CONTRACT_VALUES.includes(out[k].trim())) out[k] = "";
    if (dropBlankParties && k in out && !clean(out[k])) delete out[k];
  }
  return out;
}

/** Fills only blank party fields of `sections` from `live`; returns the same object if nothing changed. */
export function fillBlankParties(sections, live) {
  let next = sections;
  for (const k of PARTY_KEYS) {
    if (!clean(sections[k]) && live[k]) {
      if (next === sections) next = { ...sections };
      next[k] = live[k];
    }
  }
  return next;
}

/**
 * @param {{ getAttr: (k: string) => string, project: object, firm?: object|null }} args
 * firm = tenant AdvisoryFirm row (firmName, legalName, pan, gstin). Only Lead Advisor / admin can
 * read it, so for other viewers it is null and the saved draft supplies the advisor values.
 */
export function buildContractParties({ getAttr, project, firm }) {
  return {
    companyName: clean(getAttr("legalName")) || clean(project?.companyName),
    companyPAN: clean(getAttr("pan")),
    companyGST: clean(getAttr("gstn")),
    advisorName: clean(firm?.legalName) || clean(firm?.firmName),
    advisorPAN: clean(firm?.pan),
    advisorGST: clean(firm?.gstin),
    // logoUrl is a short-lived SAS URL from the API: used as-is for display, never persisted.
    advisorLogoUrl: clean(firm?.logoUrl),
  };
}

export const REQUIRED_CONTRACT_PARTY_FIELDS = Object.freeze([
  ["companyName", "Company legal name"],
  ["companyPAN", "Company PAN"],
  ["companyGST", "Company GSTIN"],
  ["advisorName", "Advisory firm name"],
  ["advisorPAN", "Advisory firm PAN"],
  ["advisorGST", "Advisory firm GSTIN"],
]);

/** Labels of required party fields that are still empty (blocks "Send for review"). */
export function missingContractParties(sections) {
  const s = sections && typeof sections === "object" ? sections : {};
  return REQUIRED_CONTRACT_PARTY_FIELDS.filter(([k]) => !clean(s[k])).map(([, label]) => label);
}

export function standardsLabel(project) {
  const list = Array.isArray(project?.standards) ? project.standards.filter(Boolean) : [];
  if (list.length) return list.join(", ");
  return clean(project?.certification);
}

/** Scope text derived only from project.standards + locations; no standard is assumed. */
export function buildScopeOfWork({ project, getAttr, locationDetails, locSummary }) {
  const standards = standardsLabel(project);
  const company = clean(project?.companyName) || clean(getAttr("legalName"));
  const lines = [
    "Scoping summary (from Summary tab):",
    `- Target framework: ${standards || "-"}`,
    `- Locations in scope: ${locationDetails.length || "-"}`,
    `- Location detail: ${locSummary}`,
    "",
  ];
  if (standards) {
    lines.push(`${standards} implementation and certification readiness${company ? ` for ${company}` : ""}.`);
  }
  return lines.join("\n");
}

export function buildLocationSummary(locationDetails) {
  return locationDetails.length
    ? locationDetails.map((l) => [l.city, l.country].filter(Boolean).join(", ") || l.country || "-").join("; ")
    : "-";
}

/** "<n> days" from milestones, or an em dash — never a made-up default. */
export function buildDurationLabel(milestoneItems) {
  if (!Array.isArray(milestoneItems) || milestoneItems.length === 0) return "-";
  const total = milestoneItems.reduce((s, m) => s + (Number(m.duration) || 0), 0);
  return total > 0 ? `${total} days` : "-";
}

/**
 * Advisor name for client documents: a real value saved in the contract draft (legacy fabricated
 * placeholders ignored), else the AdvisoryFirm record's firmName, else "" (never a made-up name).
 */
export function resolveAdvisorDisplayName(contract, firm) {
  const saved = clean(contract?.advisorName);
  if (saved && !LEGACY_FABRICATED_CONTRACT_VALUES.includes(saved)) return saved;
  return clean(firm?.firmName) || clean(firm?.legalName);
}
